import { fetchEventSource } from '@microsoft/fetch-event-source';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { getAllBaseProfiles, getGeneralProfileResults } from 'API/base-profile';
import { createBoosterProject } from 'API/booster-project';
import { createHrProject, getHrProjectAnalysis, getHrProjectByOccupation } from 'API/hr-project';
import { getOccupationSuggestions } from 'API/occupation';
import config from 'config';
import { AWTypes, BaseProfileTypes, Genres, MappedGenres, ProductGroups, ProfileTypes, Routes } from 'enums';
import TextGenerationAnalysisResults from 'features/text-generation/analysis/text-generation-analysis-results';
import TextGenerationEditor from 'features/text-generation/analysis/text-generation-editor';
import TextGenerationSuggestions from 'features/text-generation/analysis/text-generation-suggestions';
import EnterProjectNameDialog from 'features/text-generation/enter-project-name-dialog';
import { TextGenerationCredentials } from 'features/text-generation/genres-config';
import PlaceholderElement from 'features/text-generation/placeholder-element';
import GlobalProfileSelection from 'features/text-generation/profiles/global-profile-selection';
import TailoredProfileSelection from 'features/text-generation/profiles/tailored-profile-selection';
import SelectGenreDialog from 'features/text-generation/select-genre-dialog';
import TextGenerationInputs from 'features/text-generation/text-generation-inputs';
import TextGenerationProjectOverview from 'features/text-generation/text-generation-projects-overview/text-generation-project-overview';
import { Occupation } from 'features/text-generation/text-generation.types';
import TocGenerationInputs from 'features/text-generation/toc-generation-inputs';
import PaiStepFinished from 'features/ui/base-components/pai-step-finished';
import PaiStepPanel from 'features/ui/base-components/pai-step-panel';
import { usePrompt } from 'hooks/use-prompt';
import { useUserData } from 'hooks/use-user-data';
import i18n from 'i18n';
import _ from 'lodash';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { useNavigate } from 'react-router';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import useErrorStore from 'stores/error-store';
import useBoosterToolsGenreStore from 'stores/genre-store';
import useLimitReachedStore from 'stores/limit-reached-store';
import { makeStyles } from 'tss-react/mui';
import { AnalysisResult } from 'types/analysis-result.types';
import { Contact } from 'types/contact.types';
import { allowTextGen } from 'utils/blacklist.utils';
import { useAugmentedWriting } from '../features/augmented-writing/use-augmented-writing';

interface Props {
  profileType: ProfileTypes;
}

const LeadBoosterTextGeneration: React.FC<Props> = ({ profileType }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { token } = useUserData();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const handleOpenLimitReachedDialog = useLimitReachedStore((state) => state.handleOpenLimitReachedDialog);

  const navigate = useNavigate();
  const appBarNavigation = useAppBarNavigationStore((state) => state.activeTab);

  let typingTimer: ReturnType<typeof setTimeout>;

  const leadBoosterGenres = [
    Genres.SOCIAL_POST,
    Genres.SOCIAL_AD,
    Genres.INVITATION,
    Genres.MARKETING_NEWSLETTER,
    Genres.PRODUCT_DESCRIPTION,
    Genres.BLOG_POST,
    Genres.LANDING_PAGE,
  ];

  const hrBoosterGenres = [Genres.JOB_OFFER];
  const { marketingCampaign: genre, setMarketingCampaignGenre } = useBoosterToolsGenreStore((state) => ({
    marketingCampaign: state.marketingCampaign,
    setMarketingCampaignGenre: state.setMarketingCampaignGenre,
  }));
  const tocGenre = genre === Genres.BLOG_POST ? Genres.BLOG_POST_TOC : genre === Genres.LANDING_PAGE ? Genres.LANDING_PAGE_TOC : undefined;

  const domain: AWTypes = MappedGenres[genre || 'default'];

  const [selectGenreDialog, setSelectGenreDialog] = useState<{ open: boolean }>({ open: false });

  const [hideProfileSelection, setHideProfileSelection] = useState(false);

  const storedHideTextGeneration = localStorage.getItem('hide-text-generation');
  const isTextGenAllowed = allowTextGen();
  const [hideTextGeneration, setHideTextGeneration] = useState<boolean>(
    isTextGenAllowed ? (!_.isNil(storedHideTextGeneration) ? JSON.parse(storedHideTextGeneration) : false) : true
  );

  const [occupationSearch, setOccupationSearch] = useState<string>('');
  const [occupations, setOccupations] = useState<Occupation[]>([]);
  const [occupationsLoading, setOccupationsLoading] = useState(false);

  const [profiles, setProfiles] = useState<Contact[]>([]);
  const [selectedProfile, setSelectedProfile] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileResults, setProfileResults] = useState<AnalysisResult>();
  const profileSelected = !_.isNil(profileResults);

  const [enterProjectNameDialog, setEnterProjectNameDialog] = useState<{
    open: boolean;
    projectName: string;
    loading: boolean;
    nameTakenError?: boolean;
  }>({
    open: false,
    projectName: '',
    loading: false,
  });

  const [textGenerationCredentials, setTextGenerationCredentials] = useState<TextGenerationCredentials>({});
  const [textGenerationLoading, setTextGenerationLoading] = useState(false);

  const [generatedToc, setGeneratedToc] = useState<string>('');
  const [tocGenerated, setTocGenerated] = useState(false);
  const [tocAccepted, setTocAccepted] = useState(false);
  const [tocGenerationLoading, setTocGenerationLoading] = useState(false);

  const [selectedLanguage, setSelectedLanguage] = useState('auto-detect');
  const [isFormal, setIsFormal] = useState(true);
  const [includeEmoji, setIncludeEmoji] = useState(false);
  const [textVariants, setTextVariants] = useState<string[]>([]);
  const [hideTextGenerationInputs, setHideTextGenerationInputs] = useState(false);

  const editorRef = useRef<ReactQuill>(null);
  const editorTextRef = useRef<string>('');
  const [qEditor, setQEditor] = useState<any | null>(null);

  useEffect(() => {
    if (appBarNavigation === NavigationStates.TOOL && !_.isNil(profileResults)) {
      if (!_.isNil(editorRef)) {
        const newEditor = (editorRef as React.MutableRefObject<ReactQuill>).current?.getEditor();
        if (!_.isNil(newEditor)) {
          setQEditor(newEditor);
          setTimeout(() => {
            const text = editorTextRef.current;
            newEditor.setText(text);
          }, 225);
        }
      }
    }
  }, [appBarNavigation, profileResults]);

  useEffect(() => {
    if (appBarNavigation !== NavigationStates.TOOL && !_.isNil(qEditor)) {
      const text = qEditor.getText();
      if (text) {
        editorTextRef.current = text;
      }
    }
  }, [appBarNavigation, qEditor]);

  useEffect(() => {
    if (_.isNil(profileResults)) {
      editorTextRef.current = '';
    }
  }, [profileResults]);

  const {
    scores,
    highlights,
    grammarReplacements,
    paraphrases,
    optimizationMatch,
    analysisLoading,
    skipSuggestion,
    ignoreSuggestion,
    insertAtPosition,
    analyzeText,
    selectActiveSuggestion,
    dynamicStyles,
    highlightTexts,
    modifiedHighlightTexts,
    userDone,
    checkIfEmpty,
    resetAnalysis,
    analyzeTextQuick,
    optimizationLoading,
    autoFormatLoading,
    emptyEditor,
    clickedChartCategory,
    clickedSidebarNotesCategory,
    clickedChartCategoryKey,
    onChartCategoryClicked,
    allSuggestions,
    sidebarNotes,
    onSidebarNotesCategoryClicked,
    isSuggestionActive,
    activateSuggestionCard,
    resetEditorHighlights,
  } = useAugmentedWriting(qEditor, domain, profileResults?.profile, undefined, undefined);

  const [suggestionsActive, setSuggestionsActive] = useState(false);

  const textAnalysisLoading = optimizationLoading || autoFormatLoading || analysisLoading;

  const [pageLeaveReason, setPageLeaveReason] = useState<string>();

  const theme = useTheme();
  const downXL = useMediaQuery(theme.breakpoints.down('xl'));

  const isAnalysisPossible = () => {
    return !emptyEditor && !_.isNil(profileResults) && !_.isNil(profileResults.profile) && !textAnalysisLoading && !textGenerationLoading;
  };

  const handleEditorFocus = () => {
    if (emptyEditor) {
      setHideTextGeneration(true);
    }
  };

  useEffect(() => {
    resetTextGeneration();
  }, [profileType]);

  useEffect(() => {
    if (userDone) {
      setSuggestionsActive(false);
    }
  }, [userDone]);

  /**
   * Select genre dialog handlers
   */

  const handleOpenSelectGenreDialog = () => {
    setSelectGenreDialog({ open: true });
  };

  const handleCloseSelectGenreDialog = () => {
    setSelectGenreDialog({ open: false });
  };

  const acceptGenre = (genre: Genres) => {
    return () => {
      handleCloseSelectGenreDialog();
      setMarketingCampaignGenre(genre);
    };
  };

  useEffect(() => {
    return () => {
      setMarketingCampaignGenre(null);
    };
  }, []);

  useLayoutEffect(() => {
    if (_.isNil(genre)) {
      setTextGenerationCredentials({});
    }
  }, [genre]);

  useLayoutEffect(() => {
    if (_.isNil(genre) && appBarNavigation === NavigationStates.TOOL) {
      handleOpenSelectGenreDialog();
    } else {
      handleCloseSelectGenreDialog();
    }
  }, [genre, appBarNavigation]);

  /**
   * Page leave handlers
   */

  const isFormDirty = () => {
    return !emptyEditor && _.isNil(pageLeaveReason);
  };

  usePrompt(t('pageLeaveWarning'), isFormDirty());

  useEffect(() => {
    if (!_.isNil(pageLeaveReason)) {
      navigate(pageLeaveReason);
    }
  }, [pageLeaveReason]);

  /**
   * Misc handlers
   */

  const toggleProfileSelection = () => {
    setHideProfileSelection((prev) => !prev);
  };

  const toggleSuggestionsActive = () => {
    setSuggestionsActive((prev) => !prev);
  };

  /**
   * Individual profile handlers
   */

  const fetchProfiles = async () => {
    const res = await getAllBaseProfiles();
    if (res && !res.error) {
      const filtered: Contact[] = _.filter(res.profiles, (profile) => profile.type === BaseProfileTypes.TARGET_GROUP);
      setProfiles(filtered);
    } else {
      setProfiles([]);
      handleOpenErrorDialog(res?.error, '/profiles/base', 'get');
    }
  };

  const handleSelectedProfileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedProfile(event.target.value);

    (async () => {
      setProfileLoading(true);
      const res = await getGeneralProfileResults(event.target.value);
      if (res && !res.error && res.data) {
        if (!res.data.profile) {
          setProfileResults(undefined);
        } else {
          setProfileResults(res.data);
          setHideProfileSelection(true);
        }
      } else {
        handleOpenErrorDialog(res?.error, '/profiles/base/:id', 'get');
      }
      setProfileLoading(false);
    })();
  };

  useEffect(() => {
    fetchProfiles();
  }, []);

  /**
   * Global profile handlers
   */

  const handleOccupationSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOccupationSearch(event.target.value);
  };

  const handleSearchOccupations = async () => {
    if (occupationSearch !== '') {
      setOccupationsLoading(true);
      const res = await getOccupationSuggestions(occupationSearch, i18n.languages[0], 6);

      if (!res.error) {
        const resOccupations = res?.topSuggestions?.suggestionWithIds || [];
        setOccupations(resOccupations);
      } else {
        setOccupations([]);
        handleOpenErrorDialog(res?.error, '/linkedin/lookups/occupations', 'get');
      }
      setOccupationsLoading(false);
    } else {
      setOccupations([]);
    }
  };

  const occupationSearchKeyUp = () => {
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => {
      handleSearchOccupations();
    }, 500);
  };

  const occupationSearchKeyDown = () => {
    clearTimeout(typingTimer);
  };

  const fetchOccupationData = async (projectId: string) => {
    setProfileLoading(true);

    const res = await getHrProjectAnalysis(projectId);
    if (res && !res.error && res.data) {
      if (!res.data.jobOccupation.profile) {
        setProfileResults(undefined);
      } else {
        const resOccupationResults = res.data.jobOccupation;
        resOccupationResults.name = resOccupationResults.occupation;
        setProfileResults(resOccupationResults);
        setOccupations([]);
        setOccupationSearch(resOccupationResults.occupation);
        setHideProfileSelection(true);
      }
    } else {
      handleOpenErrorDialog(res?.error, '/projects/hr/:projectId', 'get');
    }
    setProfileLoading(false);
  };

  const handleOccupationClick = (occupationId: string) => {
    return async () => {
      const resProject = await getHrProjectByOccupation(occupationId);

      if (resProject && !resProject.error) {
        const existingProjectId = resProject.data.id;
        fetchOccupationData(existingProjectId);
      } else if (resProject.error.code === 401) {
        handleOpenLimitReachedDialog();
      } else if (resProject.error.code === 404) {
        const resCreate = await createHrProject(occupationId, occupationSearch);
        if (resCreate && !resCreate.error) {
          const newProjectId = resCreate.data.id;
          fetchOccupationData(newProjectId);
        } else {
          if (resCreate.error.code === 401) {
            handleOpenLimitReachedDialog();
          } else {
            handleOpenErrorDialog(resCreate?.error, '/projects/hr', 'post');
          }
        }
      } else {
        handleOpenErrorDialog(resProject?.error, '/projects/hr/by-occupation', 'get');
      }
    };
  };

  /**
   * Enter project name dialog handlers
   */

  const handleOpenProjectNameDialog = () => {
    setEnterProjectNameDialog({ ...enterProjectNameDialog, open: true });
  };

  const handleCloseProjectNameDialog = () => {
    setEnterProjectNameDialog({ open: false, projectName: '', loading: false, nameTakenError: undefined });
  };

  const handleProjectNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnterProjectNameDialog({ ...enterProjectNameDialog, projectName: event.target.value });
  };

  const handleAcceptProjectName = async () => {
    if (!_.isNil(genre) && !_.isNil(profileResults) && !_.isNil(profileResults.assessment) && !_.isNil(qEditor)) {
      const text = qEditor.getText();

      setEnterProjectNameDialog({ ...enterProjectNameDialog, loading: true });

      const res = await createBoosterProject(
        enterProjectNameDialog.projectName,
        profileResults.name,
        profileResults.profile,
        genre,
        profileResults.assessment.name,
        text,
        textGenerationCredentials.offer,
        textGenerationCredentials.content,
        textGenerationCredentials.problems
      );

      if (res && !res.error) {
        if (res.status !== 'project exists') {
          const projectId = res.projectId;
          setPageLeaveReason(`${Routes.LEAD_BOOSTER_DONE}?project=${projectId}`);
          handleCloseProjectNameDialog();
        } else {
          setEnterProjectNameDialog({ ...enterProjectNameDialog, nameTakenError: true, loading: false });
        }
      } else {
        setEnterProjectNameDialog({ ...enterProjectNameDialog, loading: false });
        handleOpenErrorDialog(res?.error, '/projects/boosters', 'post');
      }
    }
  };

  /**
   * Text generation handlers
   */

  const toggleHideTextGenerationInputs = () => {
    setHideTextGenerationInputs((prev) => {
      if (prev === true) {
        setSuggestionsActive(false);
      }

      return !prev;
    });
  };

  const toggleHideTextGeneration = () => {
    setHideTextGeneration((prev) => {
      if (prev === true) {
        setSuggestionsActive(false);
      }

      localStorage.setItem('hide-text-generation', JSON.stringify(!prev));

      return !prev;
    });
  };

  const handleTextGenerationCredentialsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const updatedCredentials = _.set({ ...textGenerationCredentials }, name, value);
    setTextGenerationCredentials(updatedCredentials);
  };

  const handleGeneratedTocChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGeneratedToc(event.target.value);
  };

  const handleAcceptToc = () => {
    const updatedCredentials = _.set({ ...textGenerationCredentials }, 'content.tableOfContents', generatedToc);
    setTextGenerationCredentials(updatedCredentials);
    setTocAccepted(true);
    handleGenerateText();
  };

  const handleToggleTocAccepted = () => {
    setTocAccepted((prev) => {
      if (prev === true) {
        setTocGenerated(false);
      }
      return !prev;
    });
  };

  const handleSelectedLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedLanguage(event.target.value);
  };

  const handleIsFormalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsFormal(event.target.checked);
  };

  const handleIncludeEmojiChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeEmoji(event.target.checked);
  };

  const handleGenerateText = async () => {
    if (
      !_.isNil(profileResults) &&
      !_.isNil(profileResults.name) &&
      !_.isNil(profileResults.profile) &&
      !_.isNil(profileResults.assessment) &&
      !_.isNil(qEditor)
    ) {
      let tempText = '';
      setTextGenerationLoading(true);
      setHideTextGenerationInputs(true);
      resetAnalysis();

      await fetchEventSource(`${config.apiBaseUrl}/boosterProject/getStreamingTextVariant`, {
        method: 'POST',
        headers: {
          Accept: 'text/event-stream',
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          profile: profileResults.profile,
          problems: textGenerationCredentials.problems,
          offer: textGenerationCredentials.offer,
          content: textGenerationCredentials.content,
          genre: genre,
          variants: textVariants,
          occupation: profileResults.name,
          dataLang: selectedLanguage,
          isFormal: isFormal,
          includeEmoji: includeEmoji,
        }),
        openWhenHidden: true,
        onopen(res): any {
          if (res.ok && res.status === 200) {
            // do nothing
          } else if (res.status >= 400 && res.status < 500 && res.status !== 429) {
            console.log('Client side error ', res);
          }
        },
        onmessage(event) {
          const parsedData: { response: string; responseType: string } = JSON.parse(event.data);
          const streamUnit: string = parsedData?.response ?? '';

          if (tempText === '' && streamUnit === '\n') {
            tempText += '';
          } else {
            tempText += streamUnit;
          }

          qEditor.setText(tempText);
        },
        onclose() {
          const updateGeneratedTexts = [...textVariants];
          updateGeneratedTexts.push(tempText);
          setTextVariants(updateGeneratedTexts);
          analyzeTextQuick();

          console.log('Connection closed by the server');
        },
        onerror(err) {
          console.log('Error: ', err);
          handleOpenErrorDialog(err, '/boosterProject/getStreamingTextVariant', 'post');
        },
      });
      setTextGenerationLoading(false);
    }
  };

  const handleGenerateToc = async () => {
    if (!_.isNil(profileResults) && !_.isNil(profileResults.name) && !_.isNil(profileResults.profile) && !_.isNil(profileResults.assessment)) {
      let tempText = '';
      setTocGenerationLoading(true);

      await fetchEventSource(`${config.apiBaseUrl}/boosterProject/getStreamingTextVariant`, {
        method: 'POST',
        headers: {
          Accept: 'text/event-stream',
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          profile: profileResults.profile,
          offer: textGenerationCredentials.offer,
          content: textGenerationCredentials.content,
          genre: tocGenre,
          dataLang: selectedLanguage,
        }),
        openWhenHidden: true,
        onopen(res): any {
          if (res.ok && res.status === 200) {
            setTocGenerated(true);
          } else if (res.status >= 400 && res.status < 500 && res.status !== 429) {
            console.log('Client side error ', res);
          }
        },
        onmessage(event) {
          const parsedData: { response: string; responseType: string } = JSON.parse(event.data);
          const streamUnit: string = parsedData?.response ?? '';

          if (tempText === '' && streamUnit === '\n') {
            tempText += '';
          } else {
            tempText += streamUnit;
          }

          setGeneratedToc(tempText);
        },
        onclose() {
          console.log('Connection closed by the server');
        },
        onerror(err) {
          console.log('Error: ', err);
          handleOpenErrorDialog(err, '/boosterProject/getStreamingTextVariant', 'post');
        },
      });

      setTocGenerationLoading(false);
    }
  };

  const resetTextGeneration = () => {
    //profile selection
    setSelectedProfile('');
    setOccupationSearch('');
    setProfileResults(undefined);
    setHideProfileSelection(false);
    // text generation
    setMarketingCampaignGenre(null);
    setTextGenerationCredentials({});
    setHideTextGenerationInputs(false);
    setSelectedLanguage('auto-detect');
    setIsFormal(true);
    setIncludeEmoji(false);
    if (!_.isNil(qEditor)) {
      qEditor.setText('');
    }
    setTextVariants([]);
    // optimization
    resetAnalysis();
  };

  return (
    <>
      {appBarNavigation === NavigationStates.TOOL ? (
        <Stack className={classes.container} direction={{ xs: 'column', xl: 'row' }} gap={1}>
          {hideProfileSelection ? (
            <PaiStepFinished stepCount={1} text={t('selectProfile')} breakpoint={'xl'} action={toggleProfileSelection} />
          ) : (
            <PaiStepPanel
              stepCount={1}
              stepName={t('selectProfile')}
              variant={'small'}
              style={{ maxWidth: downXL ? '100%' : '382px' }}
              collapse={profileSelected ? toggleProfileSelection : undefined}
            >
              {profileType === ProfileTypes.GLOBAL ? (
                <GlobalProfileSelection
                  occupationSearch={occupationSearch}
                  occupations={occupations}
                  occupationsLoading={occupationsLoading}
                  assessment={profileResults?.assessment}
                  profileLoading={profileLoading}
                  handleOccupationSearchChange={handleOccupationSearchChange}
                  occupationSearchKeyUp={occupationSearchKeyUp}
                  occupationSearchKeyDown={occupationSearchKeyDown}
                  handleOccupationClick={handleOccupationClick}
                />
              ) : (
                <TailoredProfileSelection
                  profiles={profiles}
                  selectedProfile={selectedProfile}
                  assessment={profileResults?.assessment}
                  profileLoading={profileLoading}
                  handleSelectedProfileChange={handleSelectedProfileChange}
                />
              )}
            </PaiStepPanel>
          )}
          <PaiStepPanel
            stepCount={2}
            stepName={t('generateAndFinalizeText')}
            variant={undefined}
            style={{ width: downXL ? '100%' : hideProfileSelection ? 'calc(100% - 83px)' : 'calc(100% - 390px)' }}
          >
            {profileSelected ? (
              <Stack className={classes.columns} direction={{ xs: 'column', xl: 'row' }} sx={{ width: '100%' }} gap={1}>
                {!hideTextGeneration ? (
                  (genre === Genres.BLOG_POST || genre === Genres.LANDING_PAGE) && !tocAccepted ? (
                    <TocGenerationInputs
                      hideTextGeneration={hideTextGeneration}
                      selectedLanguage={selectedLanguage}
                      genre={genre}
                      textGenerationCredentials={textGenerationCredentials}
                      generatedToc={generatedToc}
                      tocGenerated={tocGenerated}
                      tocGenerationLoading={tocGenerationLoading}
                      handleToggleTocAccepted={handleToggleTocAccepted}
                      toggleHideTextGeneration={toggleHideTextGeneration}
                      handleTextGenerationCredentialsChange={handleTextGenerationCredentialsChange}
                      handleSelectedLanguageChange={handleSelectedLanguageChange}
                      handleGeneratedTocChange={handleGeneratedTocChange}
                      handleGenerateToc={handleGenerateToc}
                      handleAcceptToc={handleAcceptToc}
                    />
                  ) : (
                    <TextGenerationInputs
                      hideTextGenerationInputs={hideTextGenerationInputs}
                      hideTextGeneration={hideTextGeneration}
                      emptyEditor={emptyEditor}
                      selectedLanguage={selectedLanguage}
                      isFormal={isFormal}
                      includeEmoji={includeEmoji}
                      genre={genre}
                      textGenerationCredentials={textGenerationCredentials}
                      textGenerationLoading={textGenerationLoading}
                      textAnalysisLoading={textAnalysisLoading}
                      handleToggleTocAccepted={handleToggleTocAccepted}
                      toggleHideTextGeneration={toggleHideTextGeneration}
                      toggleHideTextGenerationInputs={toggleHideTextGenerationInputs}
                      handleTextGenerationCredentialsChange={handleTextGenerationCredentialsChange}
                      handleSelectedLanguageChange={handleSelectedLanguageChange}
                      handleIsFormalChange={handleIsFormalChange}
                      handleIncludeEmojiChange={handleIncludeEmojiChange}
                      handleGenerateText={handleGenerateText}
                    />
                  )
                ) : null}
                <Stack
                  sx={(theme) => ({
                    width: `calc(100% ${hideTextGenerationInputs ? '- 64px' : !hideTextGeneration ? '- 358px' : ''} ${
                      suggestionsActive ? '- 288px' : ''
                    } - 288px)`,
                    [theme.breakpoints.down('xl')]: {
                      width: '100%',
                    },
                  })}
                >
                  <TextGenerationEditor
                    ref={editorRef}
                    highlights={highlights}
                    grammarReplacements={grammarReplacements}
                    paraphrases={paraphrases}
                    optimizationMatch={optimizationMatch}
                    dynamicStyles={dynamicStyles}
                    highlightTexts={highlightTexts}
                    modifiedHighlightTexts={modifiedHighlightTexts}
                    textAnalysisLoading={textAnalysisLoading}
                    clickedChartCategory={clickedChartCategory}
                    clickedSidebarNotesCategory={clickedSidebarNotesCategory}
                    hideProfileSelection={hideProfileSelection}
                    hideTextGeneration={hideTextGeneration}
                    textGenerationLoading={textGenerationLoading}
                    toggleHideTextGeneration={toggleHideTextGeneration}
                    selectActiveSuggestion={selectActiveSuggestion}
                    checkIfEmpty={checkIfEmpty}
                    insertAtPosition={insertAtPosition}
                    skipSuggestion={skipSuggestion}
                    ignoreSuggestion={ignoreSuggestion}
                    analyzeText={analyzeText}
                    analyzeTextQuick={analyzeTextQuick}
                    isAnalysisPossible={isAnalysisPossible}
                    handleEditorFocus={handleEditorFocus}
                  />
                </Stack>
                {suggestionsActive ? (
                  <TextGenerationSuggestions
                    allSuggestions={allSuggestions}
                    sidebarNotes={sidebarNotes}
                    loading={textAnalysisLoading || textGenerationLoading}
                    skipSuggestion={skipSuggestion}
                    ignoreSuggestion={ignoreSuggestion}
                    insertAtPosition={insertAtPosition}
                    onSidebarNotesCategoryClicked={onSidebarNotesCategoryClicked}
                    isSuggestionActive={isSuggestionActive}
                    activateSuggestionCard={activateSuggestionCard}
                    resetEditorHighlights={resetEditorHighlights}
                  />
                ) : null}
                <TextGenerationAnalysisResults
                  genre={genre}
                  scores={scores}
                  allSuggestions={allSuggestions}
                  sidebarNotes={sidebarNotes}
                  userDone={userDone}
                  contactAssessmentName={profileResults?.assessment?.name}
                  profileName={profileResults?.name}
                  textGenerationLoading={textGenerationLoading}
                  optimizationLoading={optimizationLoading}
                  autoFormatLoading={autoFormatLoading}
                  analysisLoading={analysisLoading}
                  clickedChartCategoryKey={clickedChartCategoryKey}
                  suggestionsActive={suggestionsActive}
                  hideTextGeneration={hideTextGeneration}
                  hideTextGenerationInputs={hideTextGenerationInputs}
                  handleOpenProjectNameDialog={handleOpenProjectNameDialog}
                  onChartCategoryClicked={onChartCategoryClicked}
                  toggleSuggestionsActive={toggleSuggestionsActive}
                />
              </Stack>
            ) : (
              <PlaceholderElement text={t('textGenPlaceholder')} />
            )}
          </PaiStepPanel>
        </Stack>
      ) : appBarNavigation === NavigationStates.PROJECTS ? (
        <div className={classes.projects}>
          <Stack gap={{ xs: 2, md: 1 }}>
            {hrBoosterGenres.map((genre) => (
              <TextGenerationProjectOverview
                key={genre}
                title={t(genre)}
                genre={genre}
                productGroup={ProductGroups.LEAD_BOOSTER}
                resetTextGeneration={resetTextGeneration}
              />
            ))}
          </Stack>
        </div>
      ) : null}

      <EnterProjectNameDialog
        open={enterProjectNameDialog.open}
        projectName={enterProjectNameDialog.projectName}
        loading={enterProjectNameDialog.loading}
        nameTakenError={enterProjectNameDialog.nameTakenError}
        handleProjectNameChange={handleProjectNameChange}
        handleClose={handleCloseProjectNameDialog}
        acceptProjectName={handleAcceptProjectName}
      />
      <SelectGenreDialog open={selectGenreDialog.open} genres={leadBoosterGenres} acceptGenre={acceptGenre} />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: '8px',
    background: theme.palette.backgrounds.main,
    height: 'calc(100vh - 60px - 32px)',
    maxHeight: 'calc(960px - 60px - 32px)',
    width: '100%',
    [theme.breakpoints.down('xl')]: {
      height: 'fit-content',
      maxHeight: 'unset',
    },
  },
  columns: {
    height: 'calc(100% - 39px - 16px - 20px)',
    width: '100%',
  },
  projects: {
    maxWidth: 'calc(1920px - 266px)',
    padding: '20px 16px 40px 48px',
    [theme.breakpoints.down('lg')]: {
      padding: '24px 8px 40px 8px',
      margin: 0,
    },
    [theme.breakpoints.down('sm')]: {
      padding: '16px 8px 32px 8px',
    },
  },
}));

export default LeadBoosterTextGeneration;
